import React from 'react';
import { FormattedHTMLMessage, InjectedIntlProps, injectIntl } from 'react-intl';

import {
    VerificationService,
    SuccessResponse,
    closeTabRef,
    Country,
} from '@sheerid/jslib';
import { getQueryParamsByName, sendPostMessage } from '../../lib/utils/browser';
import { SoftwareType } from '../../lib/types/types';
import {
    SoftwareTypesEnum,
} from '../../lib/types/runtimeTypes';
import { INTUIT_EDUCATION_URL, REPOSITION_MESSAGE } from '../../constants';

const StepSuccessCreateAccount = ({
    softwareType,
    country,
    defaultCTAMessage,
    accessUrl,
}: {
    softwareType: SoftwareType;
    country: Country;
    defaultCTAMessage: string;
    accessUrl: string;
}) => {
    const messageId = `step.success.${softwareType}.createAccount${country}`;

    return (
        <div className="sid-l-space-top-sm">
            <FormattedHTMLMessage
                id={messageId}
                defaultMessage={defaultCTAMessage}
                values={{
                    accessLink: accessUrl,
                }}
            />
        </div>
    );
};

interface StepSuccessProps {
    verificationService: VerificationService;
}

const StepSuccess = ({ verificationService, intl }: StepSuccessProps & InjectedIntlProps) => {
    // Close any open tab for SSO login
    closeTabRef();

    // the following is deemed as an unexpected use of a template literal, but it is expected for us
    // eslint-disable-next-line
    const rewardCode = (verificationService.verificationResponse as SuccessResponse).rewardCode || '${rewardCode}';
    const softwareType = getQueryParamsByName('type') as SoftwareType;

    const country = getQueryParamsByName('country') as Country;

    React.useEffect(() => {
        // Send postMessage so that Intuit's site can reposition the scroll position of the user
        sendPostMessage(REPOSITION_MESSAGE, 'https://www.intuit.com');
    }, []);

    return (
        <div id="sid-step-success" className="sid-l-container">
            <div className="sid-header sid-l-space-top-md">
                {
                    (softwareType === SoftwareTypesEnum.TAX)
                        ? (
                            <>
                                { country === 'US'
                                    ? (
                                        <div className="sid-header__title sid-l-space-top-md">
                                            <FormattedHTMLMessage id={`step.success.${softwareType}.titleUS`} defaultMessage="Success! You've been verified." />
                                        </div>
                                    )
                                    : (
                                        <div className="sid-header__title sid-l-space-top-md">
                                            <FormattedHTMLMessage id={`step.success.${softwareType}.title`} defaultMessage="Success! You've been verified." />
                                        </div>
                                    )}
                            </>
                        )
                        : (
                            <div className="sid-header__title sid-l-space-top-md">
                                <FormattedHTMLMessage id={`step.success.${softwareType}.title`} defaultMessage="Success! You've been verified." />
                            </div>
                        )
                }

                {
                    (softwareType === SoftwareTypesEnum.ONLINE) && country === 'US' ? (
                        <div className="sid-header__subtitle">
                            <FormattedHTMLMessage
                                id={`step.success.${softwareType}.subtitleUS`}
                                defaultMessage="We have successfully verified your student credentials and your free 1-year trial of QuickBooks Online Plus is ready!"
                            />
                        </div>
                    )
                        : (
                            <>
                                {
                                    (softwareType === SoftwareTypesEnum.TAX && country === 'US') ? (
                                        <div className="sid-header__subtitle">
                                            <FormattedHTMLMessage
                                                id={`step.success.${softwareType}.subtitleUS`}
                                                defaultMessage="We verified your enrollment status. Here is a 1-year trial!"
                                            />
                                        </div>
                                    ) : (
                                        <div className="sid-header__subtitle">
                                            <FormattedHTMLMessage
                                                id={`step.success.${softwareType}.subtitle`}
                                                defaultMessage="We verified your enrollment status. Here is a 1-year trial!"
                                            />
                                        </div>
                                    )
                                }
                            </>
                        )
                }
            </div>

            <StepSuccessCreateAccount
                softwareType={softwareType}
                country={country}
                defaultCTAMessage={intl.formatHTMLMessage({ id: 'step.success.createAccount', defaultMessage: '<a class="sid-link" href="{accessLink}">Create your account</a>' })}
                accessUrl={intl.formatHTMLMessage(
                    {
                        id: `step.success.${softwareType}.downloadLink${country}`,
                        defaultMessage: rewardCode,
                    },
                    { rewardCode },
                )}
            />

            {
                (country === 'US') ? (
                    <div className="sid-l-space-top-md">
                        <FormattedHTMLMessage
                            id={`step.success.${softwareType}.bestWishesUS`}
                            defaultMessage="If you have any questions, please visit our <a href='https://www.intuit.com/partners/education-program/support/' class='sid-link'>support page</a>."
                        />
                    </div>
                )
                    : (
                        <div className="sid-l-space-top-md">
                            <FormattedHTMLMessage
                                id={`step.success.${softwareType}.bestWishes`}
                                defaultMessage="Best wishes on your learning journey!"
                            />
                        </div>

                    )
            }

            {
                (country === 'US') ? (
                    <div className="sid-l-space-top-md">
                        <FormattedHTMLMessage
                            id="intuitEducationTeamUS"
                            defaultMessage="Thanks!<br>The Intuit Team"
                        />
                    </div>
                )
                    : (
                        <div className="sid-l-space-top-md">
                            <FormattedHTMLMessage
                                id="intuitEducationTeam"
                                defaultMessage="Thanks!<br>The Intuit Team"
                            />
                        </div>

                    )
            }

            <hr className="sid-success-rule sid-l-space-top-lg" />

            {
                (softwareType === SoftwareTypesEnum.TAX && country === 'CA') ? (
                    <div className="sid-l-space-top-lg">
                        <FormattedHTMLMessage
                            id="contactIntuit"
                            defaultMessage="If that link doesn't work, <a class='sid-link' href='mailto:iepcanada@intuit.com'>Education Support</a> can help."
                        />
                    </div>
                )
                    : (
                        <div className="sid-l-space-top-lg">
                            <FormattedHTMLMessage
                                id="contactIntuitUS"
                                defaultMessage="If that link doesn't work, <a class='sid-link' href='mailto:education@intuit.com'>Education Support</a> can help."
                            />
                        </div>
                    )
            }

            <div className="sid-footer-logo">
                <a href={INTUIT_EDUCATION_URL}>
                    <img className="sid-footer-logo-img" src="https://assets-resources.sheerid.com/intuit/img/intuit-cornerstone-brand-lockup-2-line-color.png" alt="Intuit Education" />
                </a>
            </div>
        </div>
    );
};


export const StepSuccessComponent = injectIntl(StepSuccess);

